import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronDown } from 'lucide-react';
import BASE_API_URL from '../../../config/baseApi';

export default function Categories() {
  const [expandedCategory, setExpandedCategory] = useState('mealPlan');

  const [categories, setCategories] = useState({
    mealPlan: [],
    roomTypes: [],
    roomCategory: [],
    guestFrom: [],
    expensesList: [],
    departmentList: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [newItems, setNewItems] = useState({
    mealPlan: '',
    roomTypes: '',
    roomCategory: '',
    guestFrom: '',
    expensesList: '',
    departmentList: ''
  });

  const categoryCodeMap = {
    mealPlan: 'MP',
    roomTypes: 'RT',
    roomCategory: 'RC',
    guestFrom: 'GF',
    expensesList: 'EX',
    departmentList: 'DEP'
  };

  const categoryConfig = [
    { key: 'mealPlan', label: 'Meal Plan', icon: '🍽️' },
    { key: 'roomTypes', label: 'Room Types', icon: '🛏️' },
    { key: 'roomCategory', label: 'Room Category', icon: '📋' },
    { key: 'guestFrom', label: 'Guest From', icon: '👥' },
    { key: 'expensesList', label: 'Expenses List', icon: '💰' },
    { key: 'departmentList', label: 'Department List', icon: '🏢' }
  ];

  // ── helper: get the auth token the same way every other page does ──
  const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BASE_API_URL}/api/categories`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();

      if (data.success && data.data) {
        setCategories(data.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── ADD ──────────────────────────────────────────────────────────────
  const handleAddItem = async (categoryKey) => {
    const name = newItems[categoryKey].trim();
    if (!name) return;

    try {
      setError(null);

      const response = await fetch(`${BASE_API_URL}/api/categories`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: name,
          cat_code: categoryCodeMap[categoryKey]
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // The controller returns 422 with a message on duplicates
        setError(data.message || 'Failed to add category');
        return;
      }

      // Append the new item returned by the API (has the real id)
      setCategories(prev => ({
        ...prev,
        [categoryKey]: [...prev[categoryKey], data.data]
      }));
      setNewItems(prev => ({ ...prev, [categoryKey]: '' }));

    } catch (err) {
      setError(err.message);
    }
  };

  // ── DELETE ────────────────────────────────────────────────────────────
  const handleDeleteItem = async (categoryKey, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      setError(null);

      const response = await fetch(`${BASE_API_URL}/api/categories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to delete category');
        return;
      }

      // Remove only from the correct category bucket using the key we already know
      setCategories(prev => ({
        ...prev,
        [categoryKey]: prev[categoryKey].filter(item => item.id !== id)
      }));

    } catch (err) {
      setError(err.message);
    }
  };

  const handleInputChange = (categoryKey, value) => {
    setNewItems(prev => ({ ...prev, [categoryKey]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Categories Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Add or delete categories for your hotel management system</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 px-4 py-3 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800 font-bold">×</button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="mb-6 px-4 py-3 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg">
            Loading categories...
          </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {categoryConfig.map((config) => (
            <div key={config.key} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Header */}
              <div
                onClick={() => setExpandedCategory(expandedCategory === config.key ? null : config.key)}
                className="p-6 bg-gradient-to-r from-blue-500 to-blue-600 cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{config.icon}</span>
                    <div>
                      <h2 className="text-xl font-bold text-white">{config.label}</h2>
                      <p className="text-sm text-blue-100">{categories[config.key].length} items</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-6 h-6 text-white transform transition-transform ${expandedCategory === config.key ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Body */}
              {expandedCategory === config.key && (
                <div className="p-6 space-y-4">
                  {/* Add input */}
                  <div className="space-y-3 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-300">
                      Add New {config.label}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newItems[config.key]}
                        onChange={(e) => handleInputChange(config.key, e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddItem(config.key)}
                        placeholder={`Enter ${config.label.toLowerCase()}...`}
                        className="flex-1 px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      <button
                        onClick={() => handleAddItem(config.key)}
                        className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Plus className="w-5 h-5" />
                        Add
                      </button>
                    </div>
                  </div>

                  {/* List */}
                  <div className="space-y-2">
                    {categories[config.key].length === 0 ? (
                      <p className="text-center py-6 text-gray-500 dark:text-gray-400">
                        No items added yet. Add one to get started!
                      </p>
                    ) : (
                      categories[config.key].map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                        >
                          <span className="text-gray-900 dark:text-gray-200 font-medium">{item.name}</span>
                          <button
                            onClick={() => handleDeleteItem(config.key, item.id)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Copyright © 2026. All right reserved.
          </p>
        </div>
      </div>
    </div>
  );
}